import React, { useState, useEffect, useCallback, useContext } from 'react';
import PageWrapper from '../components/PageWrapper';
import { AuthContext } from '../contexts/AuthContext';
import { listSubscriptions, updateSubscription } from '../services/subscriptionService';
import { formatDate } from '../utils/dateUtils';

const PLANS = ['free', 'bronze', 'silver', 'gold'];
const STATUSES = ['active', 'trial', 'expired', 'cancelled'];

const PLAN_BADGE = {
  free:   'secondary',
  bronze: 'warning',
  silver: 'info',
  gold:   'success',
};

const STATUS_BADGE = {
  active:    'success',
  trial:     'primary',
  expired:   'danger',
  cancelled: 'secondary',
};

// The /admin/subscriptions response serialises the raw ManagingEntity, whose `type`
// is a Go method (not a JSON field). Derive it from the FK / relation instead.
const entityType = (me) => {
  if (!me) return null;
  if (me.agency_id != null || me.agency) return 'agency';
  if (me.owner_id != null || me.owner) return 'owner';
  if (me.head_renter_id != null || me.head_renter) return 'head_tenant';
  return null;
};

const EntityTypeBadge = ({ type }) => {
  const colours = { agency: 'primary', owner: 'warning', head_tenant: 'info' };
  return (
    <span className={`badge bg-${colours[type] || 'secondary'} text-capitalize`}>
      {type?.replace('_', ' ') || '—'}
    </span>
  );
};

const EntityName = ({ sub }) => {
  const me = sub.managing_entity;
  if (!me) return <span className="text-muted">—</span>;
  if (me.agency)       return <span>{me.agency.name}</span>;
  if (me.owner?.person) {
    const p = me.owner.person;
    return <span>{[p.given, p.surname].filter(Boolean).join(' ') || '—'}</span>;
  }
  if (me.head_renter?.person) {
    const p = me.head_renter.person;
    return <span>{[p.given, p.surname].filter(Boolean).join(' ') || '—'}</span>;
  }
  return <span className="text-muted">Entity #{me.id}</span>;
};

const EditModal = ({ sub, onClose, onSaved }) => {
  const [plan, setPlan]       = useState(sub.plan);
  const [status, setStatus]   = useState(sub.status);
  const [expiresAt, setExpiresAt] = useState(
    sub.expires_at ? sub.expires_at.substring(0, 10) : ''
  );
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateSubscription(sub.id, {
        plan,
        status,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Subscription</h5>
            <button className="btn-close" onClick={onClose} disabled={saving}></button>
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}

            <div className="mb-3">
              <label className="form-label fw-medium">Entity</label>
              <div><EntityName sub={sub} /> &nbsp; <EntityTypeBadge type={entityType(sub.managing_entity)} /></div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium">Plan</label>
              <select
                className="form-select"
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                disabled={saving}
              >
                {PLANS.map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={saving}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="mb-1">
              <label className="form-label fw-medium">
                Expires At <span className="text-muted fw-normal">(leave blank for perpetual)</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubscriptionManagement = () => {
  const { user, agencyCountry } = useContext(AuthContext);
  const [subscriptions, setSubscriptions] = useState([]);
  const [total, setTotal]                 = useState(0);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [editing, setEditing]             = useState(null);
  const [search, setSearch]               = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listSubscriptions();
      const data = result.data || result;
      setSubscriptions(Array.isArray(data) ? data : []);
      setTotal(result.total ?? data.length);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => {
    setEditing(null);
    load();
  };

  const filtered = subscriptions.filter((sub) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const me = sub.managing_entity;
    const name =
      me?.agency?.name ||
      [me?.owner?.person?.given, me?.owner?.person?.surname].filter(Boolean).join(' ') ||
      [me?.head_renter?.person?.given, me?.head_renter?.person?.surname].filter(Boolean).join(' ') ||
      '';
    return (
      name.toLowerCase().includes(q) ||
      sub.plan.includes(q) ||
      sub.status.includes(q) ||
      (entityType(me) || '').includes(q)
    );
  });

  return (
    <PageWrapper>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h2 className="fw-bold mb-0">
              <i className="bi bi-credit-card-2-front me-2 text-success"></i>
              Subscription Management
            </h2>
            <p className="text-muted small mb-0 mt-1">
              Manage plan assignments for all agencies, owners, and head tenants
            </p>
          </div>
          <span className="badge bg-secondary fs-6">{total} total</span>
        </div>

        {/* Search */}
        <div className="mb-3" style={{ maxWidth: 360 }}>
          <div className="input-group">
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search by name, plan or status…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-success" role="status"></div>
                <p className="text-muted mt-2 mb-0">Loading subscriptions…</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger m-3">{error}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                No subscriptions found.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Entity</th>
                      <th>Type</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Expires</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((sub) => (
                      <tr key={sub.id}>
                        <td className="fw-medium"><EntityName sub={sub} /></td>
                        <td><EntityTypeBadge type={entityType(sub.managing_entity)} /></td>
                        <td>
                          <span className={`badge bg-${PLAN_BADGE[sub.plan] || 'secondary'} text-capitalize`}>
                            {sub.plan}
                          </span>
                        </td>
                        <td>
                          <span className={`badge bg-${STATUS_BADGE[sub.status] || 'secondary'} text-capitalize`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="text-muted small">
                          {sub.expires_at
                            ? formatDate(sub.expires_at, { userPreference: user?.dateFormatPreference, agencyCountry })
                            : <span className="text-success">Perpetual</span>}
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => setEditing(sub)}
                          >
                            <i className="bi bi-pencil me-1"></i>Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <EditModal
          sub={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </PageWrapper>
  );
};

export default SubscriptionManagement;
